import { useEffect, useState } from "react"
import { AppHeader } from "@/components/dashboard/AppHeader"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Progress } from "@/components/ui/progress"
import { getAiUsage } from "@/lib/ai-usage-api"
import { formatBillingDate } from "@/lib/billing-format"

const LOADING_STATE = { status: "loading", usage: null }
const ERROR_STATE = { status: "error", usage: null }
const JUDGEMENT_CREDIT_FORMATTER = new Intl.NumberFormat("en-GB", {
  maximumFractionDigits: 0,
})

function CreditScheduleMessage({ isExhausted, schedule }) {
  const effectiveDate = formatBillingDate(schedule?.effective_at)

  if (isExhausted) {
    const title = schedule?.type?.startsWith("trial_")
      ? "Trial Judgement Credits are exhausted."
      : "Your Judgement Credits are exhausted."
    let detail

    switch (schedule?.type) {
      case "trial_carry_forward":
        detail = "They do not reset when the trial converts to paid."
        break
      case "trial_ending":
        detail = `Your trial ends on ${effectiveDate}.`
        break
      case "reset":
        detail = `They reset on ${effectiveDate}.`
        break
      case "payment_issue":
        detail = "Billing requires attention. Manage billing to restore normal Judgement Credit renewal."
        break
      default:
        return null
    }

    return (
      <div role="alert" className="rounded-xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
        <p className="font-semibold">{title}</p>
        <p className="mt-1">{detail}</p>
      </div>
    )
  }

  switch (schedule?.type) {
    case "trial_carry_forward":
      return (
        <p className="text-sm text-muted-foreground">
          Unused Judgement Credits carry into your first paid month after successful payment.
        </p>
      )
    case "trial_ending":
      return (
        <p className="text-sm text-muted-foreground">
          Judgement Credits remain available until <span className="font-medium text-foreground">{effectiveDate}</span>.
        </p>
      )
    case "reset":
      return (
        <p className="text-sm text-muted-foreground">
          Judgement Credits reset on <span className="font-medium text-foreground">{effectiveDate}</span>.
        </p>
      )
    case "payment_issue":
      return (
        <p className="text-sm text-muted-foreground">
          Billing requires attention. Manage billing to restore normal Judgement Credit renewal.
        </p>
      )
    default:
      return null
  }
}

export function AiUsageScreen({ loadUsage = getAiUsage }) {
  const [requestState, setRequestState] = useState(LOADING_STATE)
  const [requestVersion, setRequestVersion] = useState(0)

  useEffect(() => {
    let ignore = false

    const load = async () => {
      try {
        const usage = await loadUsage()
        if (!ignore) setRequestState({ status: "loaded", usage })
      } catch {
        if (!ignore) setRequestState(ERROR_STATE)
      }
    }

    load()
    return () => {
      ignore = true
    }
  }, [loadUsage, requestVersion])

  const handleRetry = () => {
    setRequestState(LOADING_STATE)
    setRequestVersion((version) => version + 1)
  }
  const usage = requestState.usage
  const isExhausted = usage?.remaining_percentage === 0

  return (
    <main className="min-h-screen bg-background text-foreground">
      <title>AI Usage — Sentinel OS</title>
      <AppHeader status="online" showNav />

      <section className="mx-auto flex w-full max-w-[800px] flex-col gap-8 p-4 duration-500 animate-in fade-in slide-in-from-bottom-4 md:p-8 lg:p-12">
        <div>
          <p className="text-xs font-medium uppercase tracking-widest text-primary">AI Usage</p>
          <h1 className="mt-2 text-3xl font-bold tracking-tight">Your Sentinel Judgement Credits</h1>
          <p className="mt-3 text-muted-foreground">
            Track Judgement Credits included with your subscription.
          </p>
        </div>

        {requestState.status === "loading" ? (
          <Card aria-live="polite">
            <CardHeader>
              <CardTitle>Loading AI usage</CardTitle>
              <CardDescription>Checking your current allowance and credit schedule.</CardDescription>
            </CardHeader>
          </Card>
        ) : null}

        {requestState.status === "error" ? (
          <Card role="alert">
            <CardHeader>
              <CardTitle>AI usage unavailable</CardTitle>
              <CardDescription>AI usage is temporarily unavailable. Please try again.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button type="button" variant="outline" onClick={handleRetry}>Try again</Button>
            </CardContent>
          </Card>
        ) : null}

        {requestState.status === "loaded" ? (
          <Card>
            <CardHeader>
              <CardDescription>Current allowance</CardDescription>
              <CardTitle><h2>{usage.label}</h2></CardTitle>
            </CardHeader>
            <CardContent className="space-y-8">
              <dl className="grid gap-4 sm:grid-cols-3">
                <div className="rounded-xl border border-border bg-muted/30 p-4">
                  <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Total</dt>
                  <dd className="mt-2 text-2xl font-semibold">
                    {JUDGEMENT_CREDIT_FORMATTER.format(usage.total_judgement_credits)}
                  </dd>
                </div>
                <div className="rounded-xl border border-border bg-muted/30 p-4">
                  <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Consumed</dt>
                  <dd className="mt-2 text-2xl font-semibold">
                    {JUDGEMENT_CREDIT_FORMATTER.format(usage.consumed_judgement_credits)}
                  </dd>
                </div>
                <div className="rounded-xl border border-primary/30 bg-primary/10 p-4">
                  <dt className="text-xs font-medium uppercase tracking-wider text-primary">Remaining</dt>
                  <dd className="mt-2 text-2xl font-semibold">
                    {JUDGEMENT_CREDIT_FORMATTER.format(usage.remaining_judgement_credits)}
                  </dd>
                </div>
              </dl>

              <div className="space-y-3">
                <div className="flex items-center justify-between gap-4 text-sm">
                  <span id="ai-credit-progress-label" className="font-medium">Judgement Credits remaining</span>
                  <span className="text-muted-foreground">{usage.remaining_percentage}%</span>
                </div>
                <Progress
                  value={usage.remaining_percentage}
                  aria-labelledby="ai-credit-progress-label"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={usage.remaining_percentage}
                />
              </div>

              {!isExhausted ? (
                <p className="text-sm text-muted-foreground">
                  Some AI actions may require more Judgement Credits than your available balance.
                </p>
              ) : null}

              <CreditScheduleMessage
                isExhausted={isExhausted}
                schedule={usage.credit_schedule}
              />
            </CardContent>
          </Card>
        ) : null}
      </section>
    </main>
  )
}
